import { NotificationsBell } from './NotificationsBell';
import { NotificationNavBadge } from '@/features/notifications/useNotificationFeed';
import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { PanelLeftClose, PanelLeftOpen, Search, ShieldCheck } from 'lucide-react';
import { Button, Drawer, Input, Tooltip } from '@/components/ui';
import { filterPlatformNav, PLATFORM_NAV } from '@/features/platform/nav';
import { DirtyGuardProvider, useDirtyGuard } from '@/features/platform/ui/dirtyGuard';
import '@/features/platform/ui/platform.css';
import { cx } from '@/lib/cx';
import { useMediaQuery } from '@/lib/hooks';
import { PlatformBanner, PlatformConsoleAppearanceScope } from '@/lib/platformAppearance';
import { AnnouncementBar } from './AnnouncementBar';
import { AppHeader } from './AppHeader';
import { PageSkeleton } from './StaffShell';
import { UpdatesPanel } from './UpdatesPanel';
import { UserMenu } from './UserMenu';

function PlatformConsoleHeader({ desktop, onOpenNavigation, navigationExpanded }: { desktop: boolean; onOpenNavigation: () => void; navigationExpanded?: boolean }) {
  const { guardedNavigate } = useDirtyGuard();
  return <AppHeader desktop={desktop} groups={PLATFORM_NAV} context="Platform Admin" onToggleNavigation={onOpenNavigation} navigationExpanded={navigationExpanded} navigateTo={guardedNavigate} tools={<><UpdatesPanel navigateTo={guardedNavigate} /><NotificationsBell platform navigateTo={guardedNavigate} /></>} />;
}
const COLLAPSE_KEY = 'cop.platform.sidebar.collapsed';

function PlatformSidebar({ collapsed, onNavigate, showCollapse, onToggleCollapse }: { collapsed: boolean; onNavigate?: () => void; showCollapse: boolean; onToggleCollapse?: () => void }) {
  const [query, setQuery] = useState('');
  const { isDirty, guardedNavigate } = useDirtyGuard();
  const groups = filterPlatformNav(collapsed ? '' : query);

  return (
    <aside className="sidebar" aria-label="Primary">
      <div className="sidebar-head">
        <span className="workspace-mark" aria-hidden>
          <ShieldCheck size={16} />
        </span>
        <div className="workspace-name">
          <strong>Onboarding</strong>
          <span>Platform Admin</span>
        </div>
      </div>
      {!collapsed && (
        <div className="pf-nav-search">
          <Input type="search" aria-label="Search modules" placeholder="Search modules" value={query} onChange={(e) => setQuery(e.target.value)} leading={<Search size={15} />} />
        </div>
      )}
      <nav className="nav pf-nav">
        {groups.length === 0 && <p className="pf-nav-empty">No module matches “{query}”.</p>}
        {groups.map((g) => (
          <div className="nav-group" key={g.label} role="group" aria-label={g.label}>
            {!collapsed && <div className="nav-group-label">{g.label}</div>}
            {g.items.map((item) => {
              const Icon = item.icon;
              const link = (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  aria-label={item.label}
                  className="nav-link"
                  onClick={(e) => {
                    if (isDirty()) {
                      e.preventDefault();
                      guardedNavigate(item.to);
                    }
                    onNavigate?.();
                  }}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="nav-active-bg" />}
                      <Icon aria-hidden />
                      <span>{item.label}</span>{item.to === "/platform/notifications" && <NotificationNavBadge platform />}
                    </>
                  )}
                </NavLink>
              );
              return collapsed ? (
                <Tooltip key={item.to} content={item.label} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
          </div>
        ))}
      </nav>
      <div className="sidebar-foot">
        {showCollapse && onToggleCollapse && (
          <Button variant="ghost" size="sm" className="collapse-btn" icon={collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />} onClick={onToggleCollapse} aria-expanded={!collapsed} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            Collapse
          </Button>
        )}
        <UserMenu variant="chip" />
      </div>
    </aside>
  );
}

export function PlatformShell() {
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const desktop = useMediaQuery('(min-width: 1024px)');
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        /* ignore */
      }
      return !c;
    });

  return (
    <DirtyGuardProvider>
      {/* Platform appearance settings apply only while the console is mounted. */}
      <PlatformConsoleAppearanceScope />
      <div className={cx('shell app-shell pf-shell', desktop && collapsed && 'is-collapsed')}>
        <a href="#main" className="skip-link">
          Skip to content
        </a>
        {desktop && <PlatformSidebar collapsed={collapsed} showCollapse onToggleCollapse={toggle} />}
        <div className="main">
          <PlatformBanner />
          <PlatformConsoleHeader desktop={desktop} onOpenNavigation={desktop ? toggle : () => setMobileOpen(true)} navigationExpanded={desktop ? !collapsed : mobileOpen} />
          <main id="main" tabIndex={-1} className="content">
            <AnnouncementBar />
            <motion.div key={location.pathname} className="route-fade" initial={reducedMotion ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }}>
              <Suspense fallback={<PageSkeleton />}>
                <Outlet />
              </Suspense>
            </motion.div>
          </main>
        </div>
        {!desktop && (
          <div className="mobile-nav">
            <Drawer className="pf-mobile-navigation" open={mobileOpen} onOpenChange={setMobileOpen} title="Navigation" side="left" size="sm">
              <PlatformSidebar collapsed={false} showCollapse={false} onNavigate={() => setMobileOpen(false)} />
            </Drawer>
          </div>
        )}
      </div>
    </DirtyGuardProvider>
  );
}
